import { Link } from '@tanstack/react-router';
import { Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ActivityClaims } from './activity-claims';
import { MessageBubble, MessageRow } from './message-bubble';
import type { FormSubmissionActivity } from './types';
import type { Id } from '@minty/shared/ids';

/** A form is always inbound and always the person's own words, so it renders
 *  as a plain incoming bubble with a note of which form it came through. */
export function FormSubmissionMessage({
  activity,
  contactId,
  contactName,
}: {
  activity: FormSubmissionActivity;
  contactId: Id<'contacts'>;
  contactName: string;
}) {
  const { t } = useTranslation('tasks');

  return (
    <MessageRow isOutbound={false} handledBy={activity.handledBy}>
      <div className="flex min-w-0 max-w-[520px] flex-1 flex-col gap-1.5">
        {/* Drawn rather than only filled, because a form is the one thing in
            the feed nobody spoke or wrote to us — a stranger's own words
            arriving unprompted, worth spotting without reading. */}
        <MessageBubble isOutbound={false} tone="outlined">
          <p className="text-xs font-medium text-muted-foreground">
            {activity.formName}
          </p>
          <p className="min-h-6 whitespace-pre-wrap wrap-anywhere text-sm leading-5">
            {activity.message}
          </p>
          {/* Only on the ones read out of a mail. What is on screen is the
              model's reading, so the message it read stays one click away. */}
          {activity.emailThreadId && (
            <Link
              to="/mailbox/incoming/$threadId"
              params={{ threadId: activity.emailThreadId }}
              className="mt-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <Mail className="size-3.5" strokeWidth={2.5} />
              {t('Open the original')}
            </Link>
          )}
        </MessageBubble>
        <ActivityClaims
          activityId={activity.activityId}
          submissionId={activity.submissionId}
          claims={activity.claims}
          contactId={contactId}
          contactName={contactName}
        />
      </div>
    </MessageRow>
  );
}
